import { Injectable, inject } from '@angular/core';
import { GeoBrowserService, InteractionService, MapColors } from '@next/arable-lib/geo';
import { Store, select } from '@ngrx/store';

import { EventsKey } from 'ol/events';
import { never } from 'ol/events/condition';
import { GeoJSON } from 'ol/format';
import { Select } from 'ol/interaction';
import { Vector as Layer } from 'ol/layer';
import { tile } from 'ol/loadingstrategy';
import { unByKey } from 'ol/Observable';
import { Vector } from 'ol/source';
import { Style, Fill, Stroke } from 'ol/style';
import { createXYZ } from 'ol/tilegrid';

import { CropField, SiteState } from '../../../shared/models';
import { Feature as LocalFeature, InteractionType } from '../../models';
import { InteractionLastYearsFeatureSelected } from '../../store/actions/feature.actions';
import {
  SetCropFieldsEnabled,
  SetSelectLastYearsFieldEnabled,
} from '../../store/actions/map-context.actions';
import { getSelectedLastYearsFeatures } from '../../store/selectors/feature.selectors';
import { getSelectLastYearsFieldEnabled } from '../../store/selectors/map-context.selectors';
import { getLastYearsEntities } from './../../../registration/store/selectors/crop-field.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoSelectLastYearsFieldService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);

  interactionTypes = [InteractionType.cloneFieldFromLastYear];
  private selectEventsKey: EventsKey;
  readonly source = new Vector({
    strategy: tile(createXYZ({})),
  });

  readonly style = new Style({
    stroke: new Stroke({
      color: MapColors.lastYearsStroke,
      width: 2,
    }),
    fill: new Fill({
      color: MapColors.invisible,
    }),
  });

  readonly layer = new Layer({
    source: this.source,
    style: this.style,
    zIndex: 1,
  });

  readonly select = new Select({
    toggleCondition: never,
    multi: true,
    layers: (layer) => layer === this.layer,
    style: new Style({
      stroke: new Stroke({
        color: MapColors.lastYearsStroke,
        width: 2,
      }),
      fill: new Fill({
        color: MapColors.lastYearsFill,
      }),
      zIndex: 5,
    }),
  });

  init(geoService: GeoBrowserService) {
    geoService.map.addLayer(this.layer);
    geoService.map.addInteraction(this.select);
    this.select.setActive(false);
    this.layer.setMaxResolution(geoService.map.getView().getResolutionForZoom(13));

    const reader = new GeoJSON();

    this.store.pipe(select(getLastYearsEntities)).subscribe((featureEntities) => {
      this.source.clear();
      featureEntities.forEach((featureEntity) => {
        if (featureEntity?.feature) {
          const feature = reader.readFeature(featureEntity.feature);
          feature.setProperties({
            cropId: featureEntity.cropId,
            cropVarietyId: featureEntity.cropVarietyId,
            soilTypeId: featureEntity.soilTypeId,
            name: featureEntity.name,
            areaInHectares: featureEntity.areaInHectares,
            isSkal: featureEntity.isSkal,
            isPlanetProof: featureEntity.isPlanetProof,
            isSandySoil: featureEntity.isSandySoil,
            isRentedField: featureEntity.isRentedField,
            isGroundWaterPreservation: featureEntity.isGroundWaterPreservation,
            isAreaCalculated: featureEntity.isAreaCalculated,
            startDate: featureEntity.startDate,
            endDate: featureEntity.endDate,
            province: featureEntity.province,
            soilAnalysisIds: featureEntity.soilAnalysisIds,
            machiningDepthInCentimeters: featureEntity.machiningDepthInCentimeters,
          } as CropField);
          feature.setId(featureEntity.id);

          this.source.addFeature(feature);
        }
      });
    });

    this.store.pipe(select(getSelectLastYearsFieldEnabled)).subscribe((layerEnabled) => {
      if (layerEnabled && geoService.view.getZoom() < 15) {
        geoService.view.setZoom(15);
      }
      this.layer.setVisible(layerEnabled);
    });

    this.store.pipe(select(getSelectedLastYearsFeatures)).subscribe((selectedFeatures) => {
      if (!selectedFeatures || selectedFeatures.length === 0) {
        this.select.getFeatures().clear();
      }
    });
  }

  destroy(geoService: GeoBrowserService) {
    geoService.map.removeLayer(this.layer);
    geoService.map.removeInteraction(this.select);
  }

  start(_: GeoBrowserService) {
    const writer = new GeoJSON();
    this.selectEventsKey = this.select.on('select', (selectEvent) => {
      const selectedFeatures = selectEvent.selected.filter((feature) => !!feature);

      selectedFeatures.sort((a, b) =>
        (a.getProperties()['startDate'] as string) > (b.getProperties()['startDate'] as string)
          ? 1
          : -1,
      );

      this.store.dispatch(
        new InteractionLastYearsFeatureSelected(
          writer.writeFeatureObject(selectedFeatures[0]) as LocalFeature,
        ),
      );
    });

    this.select.setActive(true);
    this.store.dispatch(new SetCropFieldsEnabled(true));
    this.store.dispatch(new SetSelectLastYearsFieldEnabled(true));
  }

  stop(_: GeoBrowserService) {
    unByKey(this.selectEventsKey);
    this.select.setActive(false);
    this.layer.setStyle(this.style);
    this.select.getFeatures().clear();
    this.store.dispatch(new SetSelectLastYearsFieldEnabled(false));
  }
}
